import { ActionType, getType } from 'typesafe-actions';

import { ClassTag } from '../../pages/ReportCenter/types';

import * as reportCenterActions from './actions';
import {
  SavedReportsCluster,
  SavedReportsDefinition,
  SavedReportsFrequency,
  ScheduledWidget,
  ScheduledWidgetCluster,
  ScheduledWidgetDefinition,
  ScheduledWidgetFrequency,
} from './actions';
import { getNewClusterDataThunk } from './thunk';
import { Cluster, Frequency, Reporttype } from './types';

// Define the union type for all possible actions
type ReportCenterActionType = ActionType<typeof reportCenterActions>;

export interface ReportCenterState {
  classTagsTable: {
    data: ClassTag[];
    loading: boolean;
  };
  clustersTable: {
    data: Cluster[];
    loading: boolean;
  };
  clusters: {
    data: Cluster[];
    loading: boolean;
  };
  frequencies: Frequency[];
  emails: string[];
  Reporttype: Reporttype[];
  getClassTagsTableLoading: boolean;
  updateClassTagLoading: boolean;
  getClustersTableLoading: boolean;
  updateClusterLoading: boolean;

  savedClusters: SavedReportsCluster[];
  savedFrequencies: SavedReportsFrequency[];
  savedEmails: string[];
  savedDefinitions: SavedReportsDefinition[];
  savedMonteCarloReport: SavedReportsDefinition | null;
  getSavedReportsForClusterDataLoading: boolean;

  // --- Scheduled widgets ---
  scheduledWidgets: {
    data: ScheduledWidget[];
    loading: boolean;
  };
  scheduledWidgetClusters: ScheduledWidgetCluster[];
  scheduledWidgetFrequencies: ScheduledWidgetFrequency[];
  scheduledWidgetDefinitions: ScheduledWidgetDefinition[];
  scheduledWidgetEmails: string[];
  scheduledWidgetsReferenceDataLoading: boolean;
  scheduledWidgetMutationLoading: boolean;
}

const initialState: ReportCenterState = {
  classTagsTable: { data: [], loading: false },
  clustersTable: { data: [], loading: false },
  clusters: { data: [], loading: false },
  frequencies: [],
  emails: [],
  Reporttype: [],
  getClassTagsTableLoading: false,
  updateClassTagLoading: false,
  getClustersTableLoading: false,
  updateClusterLoading: false,

  savedClusters: [],
  savedFrequencies: [],
  savedEmails: [],
  savedDefinitions: [],
  savedMonteCarloReport: null,
  getSavedReportsForClusterDataLoading: false,

  scheduledWidgets: { data: [], loading: false },
  scheduledWidgetClusters: [],
  scheduledWidgetFrequencies: [],
  scheduledWidgetDefinitions: [],
  scheduledWidgetEmails: [],
  scheduledWidgetsReferenceDataLoading: false,
  scheduledWidgetMutationLoading: false,
};

export const reportCenterReducer = (
  state: ReportCenterState = initialState,
  action: ReportCenterActionType,
): ReportCenterState => {
  switch (action.type) {
    // --- Get Clusters (for getClustersThunk) ---
    case getType(reportCenterActions.getClusters.request):
      return {
        ...state,
        clusters: {
          ...state.clusters,
          loading: true,
        },
      };
    case getType(reportCenterActions.getClusters.success): {
      const raw = (action as any).payload?.data;
      // Try these possible places in order:
      const candidate = raw?.data ?? raw;

      let clusterReports: Cluster[] = [];

      if (Array.isArray(candidate)) {
        clusterReports = candidate as Cluster[];
      } else if (
        candidate &&
        typeof candidate === 'object' &&
        Array.isArray((candidate as any).cluster_reports)
      ) {
        clusterReports = (candidate as any).cluster_reports as Cluster[];
      } else {
        // fallback to empty array
        clusterReports = [];
      }

      return {
        ...state,
        clusters: {
          data: clusterReports,
          loading: false,
        },
      };
    }
    case getType(reportCenterActions.getClusters.failure):
      return {
        ...state,
        clusters: {
          ...state.clusters,
          loading: false,
        },
      };

    // --- Get Class Tags Table ---
    case getType(reportCenterActions.getClassTagsTableData.request):
      return {
        ...state,
        getClassTagsTableLoading: true,
        classTagsTable: {
          ...state.classTagsTable,
          loading: true,
        },
      };
    case getType(reportCenterActions.getClassTagsTableData.success):
      return {
        ...state,
        classTagsTable: {
          data: action.payload.data.data as ClassTag[],
          loading: false,
        },
        getClassTagsTableLoading: false,
      };
    case getType(reportCenterActions.getClassTagsTableData.failure):
      return {
        ...state,
        getClassTagsTableLoading: false,
      };

    // --- Update Class Tag ---
    case getType(reportCenterActions.updateClassTagData.request):
      return {
        ...state,
        updateClassTagLoading: true,
        classTagsTable: {
          ...state.classTagsTable,
          loading: true,
        },
      };
    case getType(reportCenterActions.updateClassTagData.success):
      return {
        ...state,
        updateClassTagLoading: false,
        classTagsTable: {
          ...state.classTagsTable,
          loading: false,
          data: state.classTagsTable.data.map((item) =>
            String(item.id) === String(action.payload.data.id)
              ? (action.payload.data as unknown as ClassTag)
              : item,
          ),
        },
      };
    case getType(reportCenterActions.updateClassTagData.failure):
      return {
        ...state,
        updateClassTagLoading: false,
      };

    // --- Get Clusters Table ---
    case getType(reportCenterActions.getClustersTableData.request):
      return {
        ...state,
        getClustersTableLoading: true,
      };
    case getType(reportCenterActions.getClustersTableData.success):
      return {
        ...state,
        clustersTable: {
          data: action.payload.data.data as Cluster[],
          loading: false,
        },
        getClustersTableLoading: false,
      };
    case getType(reportCenterActions.getClustersTableData.failure):
      return {
        ...state,
        getClustersTableLoading: false,
      };

    // --- Update Cluster ---
    case getType(reportCenterActions.updateClustersTableData.request):
      return {
        ...state,
        updateClusterLoading: true,
        clustersTable: {
          ...state.clustersTable,
          loading: true,
        },
      };
    case getType(reportCenterActions.updateClustersTableData.success):
      return {
        ...state,
        updateClusterLoading: false,
        clustersTable: {
          ...state.clustersTable,
          loading: false,
          data: state.clustersTable.data.map((item) =>
            item.id === action.payload.data.id
              ? (action.payload.data as unknown as Cluster)
              : item,
          ),
        },
      };
    case getType(reportCenterActions.updateClustersTableData.failure):
      return {
        ...state,
        updateClusterLoading: false,
      };

    case getType(reportCenterActions.getSavedReportsForClusterData.request):
      return {
        ...state,
        getSavedReportsForClusterDataLoading: true,
      };

    case getType(reportCenterActions.getSavedReportsForClusterData.success):
      return {
        ...state,
        savedClusters: action.payload.clusters,
        savedFrequencies: action.payload.frequencies,
        savedEmails: action.payload.emails,
        savedDefinitions: action.payload.reports,
        savedMonteCarloReport: action.payload.monte_carlo_report ?? null,
        getSavedReportsForClusterDataLoading: false,
      };

    case getType(reportCenterActions.getSavedReportsForClusterData.failure):
      return {
        ...state,
        getSavedReportsForClusterDataLoading: false,
      };

    // --- Scheduled widgets: reference data ---
    case getType(reportCenterActions.getScheduledWidgetsReferenceData.request):
      return {
        ...state,
        scheduledWidgetsReferenceDataLoading: true,
      };
    case getType(reportCenterActions.getScheduledWidgetsReferenceData.success):
      return {
        ...state,
        scheduledWidgetClusters: action.payload.clusters,
        scheduledWidgetFrequencies: action.payload.frequencies,
        scheduledWidgetDefinitions: action.payload.widgets,
        scheduledWidgetEmails: action.payload.emails,
        scheduledWidgetsReferenceDataLoading: false,
      };
    case getType(reportCenterActions.getScheduledWidgetsReferenceData.failure):
      return {
        ...state,
        scheduledWidgetsReferenceDataLoading: false,
      };

    // --- Scheduled widgets: grid ---
    case getType(reportCenterActions.getScheduledWidgets.request):
      return {
        ...state,
        scheduledWidgets: { ...state.scheduledWidgets, loading: true },
      };
    case getType(reportCenterActions.getScheduledWidgets.success):
      return {
        ...state,
        scheduledWidgets: {
          data: action.payload.scheduled_widgets,
          loading: false,
        },
      };
    case getType(reportCenterActions.getScheduledWidgets.failure):
      return {
        ...state,
        scheduledWidgets: { ...state.scheduledWidgets, loading: false },
      };

    // --- Scheduled widgets: create / update / delete ---
    // The grid is refetched by the thunks, so these only drive the busy flag.
    case getType(reportCenterActions.createScheduledWidget.request):
    case getType(reportCenterActions.updateScheduledWidget.request):
    case getType(reportCenterActions.deleteScheduledWidget.request):
      return {
        ...state,
        scheduledWidgetMutationLoading: true,
      };
    case getType(reportCenterActions.createScheduledWidget.success):
    case getType(reportCenterActions.createScheduledWidget.failure):
    case getType(reportCenterActions.updateScheduledWidget.success):
    case getType(reportCenterActions.updateScheduledWidget.failure):
    case getType(reportCenterActions.deleteScheduledWidget.success):
    case getType(reportCenterActions.deleteScheduledWidget.failure):
      return {
        ...state,
        scheduledWidgetMutationLoading: false,
      };

    case getNewClusterDataThunk.pending.type:
      return {
        ...state,
        clusters: { ...state.clusters, loading: true }, // set loading true while fetching
      };

    case getNewClusterDataThunk.rejected.type:
      return {
        ...state,
        clusters: { ...state.clusters, loading: false }, // stop loading on failure
      };

    default:
      return state;
  }
};
export default reportCenterReducer;
